/**
 * Interview Practice Types
 *
 * TypeScript interfaces for virtual mock-interview feature.
 * The candidate practises answering AI-generated questions derived from
 * the target job description, then receives a detailed debrief.
 */

/* ------------------------------------------------------------------ */
/*  Duration                                                           */
/* ------------------------------------------------------------------ */

/** Allowed interview durations (minutes). Higher values are gated. */
export type InterviewDuration = 10 | 15 | 20 | 30;

/** Maps duration → tier so the UI can grey-out premium options. */
export const DURATION_TIERS: Record<InterviewDuration, 'free' | 'premium'> = {
  10: 'free',
  15: 'free',
  20: 'premium',
  30: 'premium',
};

/* ------------------------------------------------------------------ */
/*  Session                                                            */
/* ------------------------------------------------------------------ */

export type InterviewStatus = 'setup' | 'in-progress' | 'completed' | 'cancelled';

export interface InterviewQuestion {
  id: string;
  text: string;
  category: 'behavioral' | 'technical' | 'situational' | 'role-specific';
  difficulty: 'easy' | 'medium' | 'hard';
  /** Optional follow-up the AI may ask after the initial answer. */
  followUp?: string;
}

export interface InterviewResponse {
  questionId: string;
  answer: string;
  /** Seconds the candidate spent answering this question. */
  durationSec: number;
  /** ISO timestamp when the answer was submitted. */
  answeredAt: string;
}

export interface InterviewSession {
  id: string;
  userId: string;
  status: InterviewStatus;
  /** Target position info (copied from Resume Tuner context). */
  jobPosition: {
    title: string;
    company: string;
    description: string;
  };
  durationMinutes: InterviewDuration;
  questions: InterviewQuestion[];
  responses: InterviewResponse[];
  /** ISO timestamps */
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
}

/* ------------------------------------------------------------------ */
/*  Debrief / Retro                                                    */
/* ------------------------------------------------------------------ */

export interface DebriefStrength {
  summary: string;
  /** Which question(s) demonstrated this strength. */
  questionIds: string[];
}

export interface DebriefImprovement {
  summary: string;
  /** Concrete tip the candidate can apply. */
  tip: string;
  questionIds: string[];
}

export interface LearningResource {
  title: string;
  url?: string;
  type: 'article' | 'video' | 'book' | 'technique';
  description: string;
}

export interface InterviewDebrief {
  sessionId: string;
  /** 0-100 overall score. */
  overallScore: number;
  strengths: DebriefStrength[];
  improvements: DebriefImprovement[];
  /** Suggested learning material / techniques. */
  resources: LearningResource[];
  /** Free-form closing remarks from the AI. */
  summary: string;
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/*  API payloads                                                       */
/* ------------------------------------------------------------------ */

export interface StartInterviewRequest {
  jobTitle: string;
  company: string;
  jobDescription: string;
  durationMinutes: InterviewDuration;
}

export interface StartInterviewResponse {
  success: boolean;
  session?: InterviewSession;
  error?: string;
}

export interface SubmitAnswerRequest {
  sessionId: string;
  questionId: string;
  answer: string;
  durationSec: number;
}

export interface SubmitAnswerResponse {
  success: boolean;
  /** Optional AI follow-up question or acknowledgement. */
  followUp?: string;
  /** True when the timer is up or all questions answered. */
  interviewComplete: boolean;
  error?: string;
}

export interface GenerateDebriefRequest {
  sessionId: string;
}

export interface GenerateDebriefResponse {
  success: boolean;
  debrief?: InterviewDebrief;
  error?: string;
}
